/**
 * "New note" — the page's one highest-intent action. Opens the standard
 * NoteFormModal in its cross-workspace mode: a required workspace
 * multi-select chooses the destination(s), and the note is created in each
 * selected workspace.
 */
import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../../lib/api';
import NotesV2Api from '../../../services/notesV2Api';
import NoteFormModal from '../../notes/NoteFormModal';
import { AddIcon } from '../../icons';

interface WriteWorkspace {
  id: string;
  name: string;
  organization_name: string;
}

interface NoteFormData {
  title?: string;
  content: string;
  include_in_reports: boolean;
  tags: string[];
  entities: Array<{ entity_type: string; entity_id: string; entity_display_name?: string }>;
}

const NewNoteButton: React.FC = () => {
  const [open, setOpen] = useState(false);

  const { data: workspaces = [] } = useQuery(
    ['proHome', 'writeWorkspaces'],
    async (): Promise<WriteWorkspace[]> => {
      const response = await api.get('/workspace-bill-tracking/workspaces');
      return response.data?.data ?? [];
    },
    { staleTime: 5 * 60 * 1000 },
  );

  const saveMutation = useMutation({
    mutationFn: async ({ workspaceIds, data }: { workspaceIds: string[]; data: NoteFormData }) => {
      const results = await Promise.allSettled(
        workspaceIds.map((id) => NotesV2Api.createNote(id, data)),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        throw new Error(
          `The note could not be saved in ${failed} of ${workspaceIds.length} workspaces.`,
        );
      }
    },
    onSuccess: () => {
      setOpen(false);
    },
    onError: (error: Error) => {
      // Same pattern the other pages use for save failures; the modal stays
      // open so nothing typed is lost.
      alert(error.message || 'Saving the note failed. Try again.');
    },
  });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={workspaces.length === 0}
        className="inline-flex items-center gap-1.5 rounded-lg bg-action px-3.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-action-hover disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <AddIcon className="w-4 h-4" aria-hidden="true" />
        New note
      </button>

      {open && (
        <NoteFormModal
          isOpen={true}
          onClose={() => setOpen(false)}
          workspaceOptions={workspaces.map((w) => ({ id: w.id, name: w.name }))}
          isSaving={saveMutation.isLoading}
          onSaveMulti={(workspaceIds, data) => saveMutation.mutate({ workspaceIds, data })}
        />
      )}
    </>
  );
};

export default NewNoteButton;
